import { useCallback, useState } from 'react';
import { isEqual } from 'lodash';

import { BasicPaymentType, ChartOfAccountsAccount, NonCashPaymentType, SinglePaymentType } from '@futrli-sdk/predict';

import { AllowedPaymentTypes } from '../../detail/reports/actions/actions';
import { AllowedConsolidationPaymentTypes, MultiPaymentTreatment, PaymentTreatment } from '../types';
import { PAYMENT_TYPES } from '../../../core/constants';

export const defaultMultiPaymentData: MultiPaymentTreatment[] = [
  {
    payment_terms: '',
    payment_percentage: ''
  },
  {
    payment_terms: '',
    payment_percentage: ''
  },
  {
    payment_terms: '',
    payment_percentage: ''
  }
];

export const useCashFlowTreatment = () => {
  const [cashFlowImpactTouched, setCashFlowImpactTouched] = useState(false);
  const [multiPaymentData, setMultiPaymentData] = useState(defaultMultiPaymentData);
  const [nonCashAccountIndex, setNonCashAccountIndex] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [paymentTreatment, setPaymentTreatment] = useState<PaymentTreatment>(PaymentTreatment.SAME_DAY);

  const handleAddNewMultiPaymentTreatment = useCallback(() => {
    setCashFlowImpactTouched(true);
    setMultiPaymentData([...multiPaymentData, { payment_terms: '', payment_percentage: '' }]);
  }, [multiPaymentData]);

  const handleChangeMultiPaymentTreatmentData = useCallback(
    (index: number, data: MultiPaymentTreatment) => {
      const newMultiPaymentData = [
        ...multiPaymentData.slice(0, index),
        {
          ...data
        },
        ...multiPaymentData.slice(index + 1)
      ];

      if (!isEqual(newMultiPaymentData, multiPaymentData)) {
        setMultiPaymentData(newMultiPaymentData);
        setCashFlowImpactTouched(true);
      }
    },
    [multiPaymentData]
  );

  const handleNonCashAccountIndexChange = useCallback((value: ChartOfAccountsAccount | null) => {
    setNonCashAccountIndex(value ? value.code : '');
    setCashFlowImpactTouched(true);
  }, []);

  const handlePaymentTermsChange = useCallback((value: string) => {
    setPaymentTerms(value);
    setCashFlowImpactTouched(true);
  }, []);

  const handlePaymentTreatmentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newPaymentTreatment = e.target.value as PaymentTreatment;
    setPaymentTreatment(newPaymentTreatment);
    setCashFlowImpactTouched(true);
  }, []);

  const reset = useCallback(() => {
    setPaymentTreatment(PaymentTreatment.SAME_DAY);
    setPaymentTerms('');
    setNonCashAccountIndex('');
    setMultiPaymentData(defaultMultiPaymentData);
    setCashFlowImpactTouched(false);
  }, []);

  const restore = useCallback(
    (
      predictionPaymentData: AllowedPaymentTypes | AllowedConsolidationPaymentTypes,
      predictionPaymentType: PAYMENT_TYPES | undefined
    ) => {
      if (predictionPaymentType === PAYMENT_TYPES.SINGLE) {
        const singlePaymentType = predictionPaymentData as SinglePaymentType;
        if (singlePaymentType.payment_terms === 0) {
          setPaymentTreatment(PaymentTreatment.SAME_DAY);
        } else {
          setPaymentTreatment(PaymentTreatment.SINGLE);
          setPaymentTerms(String(singlePaymentType.payment_terms));
        }
      }
      if (predictionPaymentType === PAYMENT_TYPES.BASIC) {
        const mulitPaymentType = predictionPaymentData as BasicPaymentType;
        setPaymentTreatment(PaymentTreatment.MULTI_PAYMENT_DAYS);
        setMultiPaymentData(
          mulitPaymentType.payments.map(d => ({
            payment_terms: String(d.payment_terms),
            payment_percentage: d.payment_percentage
          }))
        );
      }
      if (predictionPaymentType === PAYMENT_TYPES.NON_CASH) {
        const nonCashPaymentType = predictionPaymentData as NonCashPaymentType;
        setPaymentTreatment(PaymentTreatment.NON_CASH_TRANSFER);
        setNonCashAccountIndex(nonCashPaymentType.account_id);
      }
    },
    []
  );

  return {
    cashFlowImpactTouched,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    multiPaymentData,
    nonCashAccountIndex,
    paymentTerms,
    paymentTreatment,
    reset,
    restore
  };
};
